/* global URL, Blob */
import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Empty, Input, Pagination, Popconfirm, Select, Space, Spin, Table, Tag, Tooltip } from 'antd'
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons'
import type { TableColumnsType } from 'antd'
import axios from 'axios'
import dayjs from 'dayjs'
import { appApiUrls } from 'src/shared/url-map.js'

type PageInfo = { current: number; pageSize: number; total: number; totalPages: number }

type AppInfo = { appVersion?: string; osVersion?: string; deviceModel?: string } | null

type FootprintUpload = {
  id: string
  uploadId: string
  userToken: string | null
  fileName: string
  fileExtension: string
  fileByteSize: number
  parseStatus: string
  parseError: string | null
  pointCount: number | null
  dayCount: number | null
  /** Export format the client detected; null for uploads from clients that predate the field. */
  source: string | null
  appInfo: AppInfo
  createdAt: string
  downloadUrl: string
}

/** Mirrors `FootprintImportSource` in the iOS app. */
const FOOTPRINT_SOURCE_LABELS: Record<string, string> = {
  lifetime_footprint_csv: '一生足迹 CSV',
  lifetime_footprint_gpx: '一生足迹 GPX',
  linggan_csv: '灵敢足迹',
  dot_of_life_csv: '人生点点',
  golog_gpx: 'GoLog',
  retrace_csv: '小鹿足迹 CSV',
  retrace_gpx: '小鹿足迹 GPX',
  rond_csv: 'Rond CSV',
  rond_backup: 'Rond 备份',
  gpx: '其他 GPX',
}

type DiagnosticsUpload = {
  id: string
  uploadId: string
  userToken: string | null
  rawGpsPointCount: number
  trackPointCount: number
  activityCount: number
  visitRecordCount: number
  windowStart: string
  windowEnd: string
  appInfo: AppInfo
  createdAt: string
  downloadUrl: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Downloads through an authenticated XHR rather than a plain link: the file
 * endpoints sit behind the admin session, and an `<a download>` would silently
 * save the 401 JSON body as if it were the file.
 */
async function downloadFile(url: string, fallbackName: string) {
  const res = await axios.get(url, { responseType: 'blob' })
  const disposition = res.headers['content-disposition'] as string | undefined
  const match = disposition?.match(/filename\*=UTF-8''([^;]+)/)
  const name = match?.[1] ? decodeURIComponent(match[1]) : fallbackName

  const objectUrl = URL.createObjectURL(res.data as Blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(objectUrl)
}

function useUploadList<T>(listUrl: string, extraParams: Record<string, string | undefined> = {}) {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<PageInfo>({ current: 1, pageSize: 20, total: 0, totalPages: 0 })

  // Serialized so the effect below doesn't re-run on every render of a fresh
  // object literal with identical contents.
  const paramsKey = JSON.stringify(extraParams)

  const fetchData = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true)
      try {
        const res = await axios.get(listUrl, {
          params: { page, pageSize, ...(JSON.parse(paramsKey) as Record<string, string | undefined>) },
        })
        if (res.data.code === 0) {
          setData(res.data.data.list as T[])
          setPagination(res.data.data.pagination as PageInfo)
        }
      } catch (error) {
        console.error('Failed to load upload list', error)
      } finally {
        setLoading(false)
      }
    },
    [listUrl, paramsKey],
  )

  useEffect(() => {
    void fetchData(1, pagination.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData])

  return { data, loading, pagination, fetchData }
}

function DownloadButton({ url, fallbackName }: { url: string; fallbackName: string }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      size="small"
      icon={<DownloadOutlined />}
      loading={busy}
      onClick={async () => {
        setBusy(true)
        try {
          await downloadFile(url, fallbackName)
        } catch (error) {
          console.error('Download failed', error)
        } finally {
          setBusy(false)
        }
      }}
    >
      下载
    </Button>
  )
}

function DeleteButton({ url, onDeleted }: { url: string; onDeleted: () => void }) {
  const { message } = App.useApp()
  const [busy, setBusy] = useState(false)
  return (
    <Popconfirm
      title="删除这个文件？"
      description="删除后无法恢复"
      okText="删除"
      okButtonProps={{ danger: true }}
      cancelText="取消"
      onConfirm={async () => {
        setBusy(true)
        try {
          const res = await axios.delete(url)
          if (res.data.code === 0) {
            message.success('已删除')
            onDeleted()
          } else {
            message.error(res.data.msg ?? '删除失败')
          }
        } catch (error) {
          console.error('Delete failed', error)
          message.error('删除失败')
        } finally {
          setBusy(false)
        }
      }}
    >
      <Button size="small" danger icon={<DeleteOutlined />} loading={busy}>
        删除
      </Button>
    </Popconfirm>
  )
}

/**
 * Phones get a card per row instead of the table: a 375px screen can't fit
 * a filename column plus five others, and a sideways-scrolling table hides
 * the action buttons off-screen. Desktop keeps the antd Table.
 */
function MobileCardList<T extends { id: string }>({
  data,
  loading,
  pagination,
  onPageChange,
  renderCard,
}: {
  data: T[]
  loading: boolean
  pagination: PageInfo
  onPageChange: (page: number, pageSize: number) => void
  renderCard: (record: T) => React.ReactNode
}) {
  return (
    <Spin spinning={loading}>
      {data.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100 dark:divide-slate-600">
          {data.map((record) => (
            <li key={record.id} className="flex flex-col gap-1.5 py-3">
              {renderCard(record)}
            </li>
          ))}
        </ul>
      )}
      {pagination.total > pagination.pageSize && (
        <Pagination
          simple
          className="mt-2 flex justify-center"
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={onPageChange}
        />
      )}
    </Spin>
  )
}

function ParseStatusTag({ record }: { record: FootprintUpload }) {
  return record.parseStatus === 'succeeded' ? (
    <Tag color="green">成功</Tag>
  ) : (
    <Tooltip title={record.parseError ?? ''}>
      <Tag color="red">失败</Tag>
    </Tooltip>
  )
}

function SourceTag({ source }: { source: string | null }) {
  return source ? <Tag>{FOOTPRINT_SOURCE_LABELS[source] ?? source}</Tag> : <span className="text-gray-400">—</span>
}

/**
 * The raw CSV/GPX files users picked in the footprint import flow. Despite the
 * `lifetimeFootprint` API name, this covers every supported app (一生足迹, 灵敢足迹,
 * 人生点点, GoLog …) — the name predates the other sources.
 */
export function FootprintImportUploadsTable() {
  const [keyword, setKeyword] = useState('')
  const [parseStatus, setParseStatus] = useState('all')
  const [source, setSource] = useState('all')
  const [applied, setApplied] = useState<{ keyword?: string; parseStatus?: string; source?: string }>({})
  const { data, loading, pagination, fetchData } = useUploadList<FootprintUpload>(
    appApiUrls.lifetimeFootprint.list,
    applied,
  )
  // Reload the current page so the row disappears and the pagination total updates.
  const refresh = () => void fetchData(pagination.current, pagination.pageSize)

  const pointSummary = (record: FootprintUpload) =>
    `${(record.pointCount ?? 0).toLocaleString()} 点 / ${record.dayCount ?? 0} 天`
  const deviceSummary = (record: FootprintUpload) =>
    `${record.appInfo?.appVersion ?? '—'} · ${record.appInfo?.deviceModel ?? '—'}`
  const actions = (record: FootprintUpload) => (
    <Space>
      <DownloadButton url={record.downloadUrl} fallbackName={record.fileName} />
      <DeleteButton url={appApiUrls.lifetimeFootprint.file(record.id)} onDeleted={refresh} />
    </Space>
  )

  // Every column but 文件 holds short, fixed-shape values, so they never wrap;
  // 文件 takes the leftover width and truncates with a tooltip instead.
  const columns: TableColumnsType<FootprintUpload> = [
    {
      title: '文件',
      dataIndex: 'fileName',
      ellipsis: { showTitle: false },
      render: (fileName: string, record) => (
        <div className="flex min-w-0 flex-col">
          <Tooltip title={fileName} placement="topLeft">
            <span className="truncate font-medium">{fileName}</span>
          </Tooltip>
          <span className="text-xs text-gray-400">
            {formatBytes(record.fileByteSize)} · {record.fileExtension.toUpperCase()}
          </span>
        </div>
      ),
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 120,
      render: (source: string | null) => <SourceTag source={source} />,
    },
    {
      title: '解析',
      dataIndex: 'parseStatus',
      width: 150,
      render: (_, record) => (
        <div className="flex flex-col items-start whitespace-nowrap">
          <ParseStatusTag record={record} />
          {record.parseStatus === 'succeeded' && <span className="text-xs text-gray-400">{pointSummary(record)}</span>}
        </div>
      ),
    },
    {
      title: '用户',
      dataIndex: 'userToken',
      width: 200,
      ellipsis: { showTitle: false },
      render: (userToken: string | null, record) => (
        <div className="flex min-w-0 flex-col text-xs">
          <Tooltip title={userToken} placement="topLeft">
            <span className="truncate font-mono">{userToken || '—'}</span>
          </Tooltip>
          <span className="truncate text-gray-400">{deviceSummary(record)}</span>
        </div>
      ),
    },
    {
      title: '上传时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (createdAt: string) => (
        <span className="whitespace-nowrap">{dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss')}</span>
      ),
    },
    {
      title: '操作',
      width: 170,
      fixed: 'right',
      render: (_, record) => actions(record),
    },
  ]

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Input.Search
          allowClear
          placeholder="搜索 uploadId / 用户 / 文件名 / sha256"
          className="w-full sm:w-80"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={(value) => setApplied({ keyword: value || undefined, parseStatus, source })}
        />
        <div className="flex gap-2">
          <Select
            value={source}
            className="flex-1 sm:w-36 sm:flex-none"
            onChange={(value) => {
              setSource(value)
              setApplied({ keyword: keyword || undefined, parseStatus, source: value })
            }}
            options={[
              { value: 'all', label: '全部来源' },
              ...Object.entries(FOOTPRINT_SOURCE_LABELS).map(([value, label]) => ({ value, label })),
            ]}
          />
          <Select
            value={parseStatus}
            className="flex-1 sm:w-36 sm:flex-none"
            onChange={(value) => {
              setParseStatus(value)
              setApplied({ keyword: keyword || undefined, parseStatus: value, source })
            }}
            options={[
              { value: 'all', label: '全部解析状态' },
              { value: 'succeeded', label: '解析成功' },
              { value: 'failed', label: '解析失败' },
            ]}
          />
        </div>
      </div>
      <div className="hidden md:block">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
          }}
          onChange={(page) => void fetchData(page.current ?? 1, page.pageSize ?? 20)}
        />
      </div>
      <div className="md:hidden">
        <MobileCardList
          data={data}
          loading={loading}
          pagination={pagination}
          onPageChange={(page, pageSize) => void fetchData(page, pageSize)}
          renderCard={(record) => (
            <>
              <div className="break-all font-medium">{record.fileName}</div>
              <div className="flex flex-wrap items-center gap-y-1 text-xs text-gray-400">
                {record.source && <SourceTag source={record.source} />}
                <ParseStatusTag record={record} />
                <span>
                  {formatBytes(record.fileByteSize)} · {record.fileExtension.toUpperCase()}
                  {record.parseStatus === 'succeeded' && ` · ${pointSummary(record)}`}
                </span>
              </div>
              {record.parseStatus !== 'succeeded' && record.parseError && (
                <div className="break-all text-xs text-red-400">{record.parseError}</div>
              )}
              <div className="flex flex-col text-xs text-gray-400">
                <span className="break-all font-mono text-gray-600 dark:text-gray-300">{record.userToken || '—'}</span>
                <span>
                  {deviceSummary(record)} · {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm')}
                </span>
              </div>
              <div className="pt-1">{actions(record)}</div>
            </>
          )}
        />
      </div>
    </div>
  )
}

/** Three-day device database snapshots uploaded by the diagnostics service. */
export function TestFlightDiagnosticsUploadsTable() {
  const [keyword, setKeyword] = useState('')
  const [applied, setApplied] = useState<{ keyword?: string }>({})
  const { data, loading, pagination, fetchData } = useUploadList<DiagnosticsUpload>(
    appApiUrls.testflightDiagnostics.list,
    applied,
  )

  const windowSummary = (record: DiagnosticsUpload) =>
    `${dayjs(record.windowStart).format('MM-DD')} ~ ${dayjs(record.windowEnd).format('MM-DD')}`

  const columns: TableColumnsType<DiagnosticsUpload> = [
    {
      title: 'uploadId',
      dataIndex: 'uploadId',
      render: (uploadId: string, record) => (
        <Space direction="vertical" size={0}>
          <span className="font-medium">{uploadId}</span>
          <span className="text-xs text-gray-400">
            {record.appInfo?.appVersion ?? '—'} · {record.appInfo?.osVersion ?? '—'}
          </span>
        </Space>
      ),
    },
    {
      title: '数据量',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span className="text-xs">
            TrackPoint {record.trackPointCount} · Activity {record.activityCount}
          </span>
          <span className="text-xs text-gray-400">
            RawGps {record.rawGpsPointCount} · Visit {record.visitRecordCount}
          </span>
        </Space>
      ),
    },
    {
      title: '数据窗口',
      width: 120,
      render: (_, record) => <span className="whitespace-nowrap">{windowSummary(record)}</span>,
    },
    {
      title: '上传时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (createdAt: string) => (
        <span className="whitespace-nowrap">{dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss')}</span>
      ),
    },
    {
      title: '操作',
      width: 100,
      fixed: 'right',
      render: (_, record) => <DownloadButton url={record.downloadUrl} fallbackName={`${record.uploadId}.json`} />,
    },
  ]

  return (
    <div className="flex flex-col gap-2">
      <Input.Search
        allowClear
        placeholder="搜索 uploadId / 用户"
        className="w-full sm:w-80"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onSearch={(value) => setApplied({ keyword: value || undefined })}
      />
      <div className="hidden md:block">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
          }}
          onChange={(page) => void fetchData(page.current ?? 1, page.pageSize ?? 20)}
        />
      </div>
      <div className="md:hidden">
        <MobileCardList
          data={data}
          loading={loading}
          pagination={pagination}
          onPageChange={(page, pageSize) => void fetchData(page, pageSize)}
          renderCard={(record) => (
            <>
              <div className="break-all font-medium">{record.uploadId}</div>
              <div className="text-xs text-gray-400">
                {record.appInfo?.appVersion ?? '—'} · {record.appInfo?.osVersion ?? '—'} · 窗口 {windowSummary(record)}
              </div>
              <div className="text-xs">
                TrackPoint {record.trackPointCount} · Activity {record.activityCount} · RawGps {record.rawGpsPointCount}{' '}
                · Visit {record.visitRecordCount}
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs text-gray-400">{dayjs(record.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                <DownloadButton url={record.downloadUrl} fallbackName={`${record.uploadId}.json`} />
              </div>
            </>
          )}
        />
      </div>
    </div>
  )
}
